import React from 'react';
import { useTranslation } from 'react-i18next';
import { Field } from 'react-final-form';
import PropTypes from 'prop-types';
import { InputAdornment, Tooltip, Unstable_Grid2 as Grid } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckboxFieldAdapter from 'components/shared/field-adapters/CheckboxFieldAdapter';
import ImageUploadAdapter from 'components/shared/field-adapters/ImageUploadAdapter';
import SelectFieldAdapter from 'components/shared/field-adapters/SelectFieldAdapter';
import TextFieldAdapter from 'components/shared/field-adapters/TextFieldAdapter';
import TextAreaFieldAdapter from 'components/shared/field-adapters/TextAreaFieldAdapter';
import PlanCategoryDropdown from 'components/plan/PlanCategoryDropdown';
import { getPlanTypeOptions, getPlatformOptions } from 'common/constants/options';
import { STRIPE_ENABLED } from 'common/constants/payments';
import { isRequiredField } from 'common/utils/general';

const DESCRIPTION_MAX_LENGTH = 500;

// Bloquea letras/símbolos en los campos numéricos: descarta todo lo que no sea dígito
// antes de guardarlo en el formulario (react-final-form `parse`). Así nunca llega NaN.
const onlyDigits = (value) =>
  value === undefined || value === null ? value : String(value).replace(/\D/g, '');

function PlanFields({ requiredFields }) {
  const { t } = useTranslation(['labels', 'options']);
  const planTypeOptions = getPlanTypeOptions(t);
  const platformOptions = getPlatformOptions(t);

  const numbersOnlyHint = t('onlyNumbers', { ns: 'labels' });
  const numbersOnlyAdornment = (
    <InputAdornment position="end">
      <Tooltip title={numbersOnlyHint} arrow>
        <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled', cursor: 'help' }} />
      </Tooltip>
    </InputAdornment>
  );

  return (
    <Grid container spacing={4}>
      <Grid xs={12} sm={6}>
        <Field
          id="name"
          name="name"
          component={TextFieldAdapter}
          label={t('name', { ns: 'labels' })}
          type="text"
          fullWidth
          required={isRequiredField('name', requiredFields)}
        />
      </Grid>
      <Grid xs={12} sm={6}>
        <Field
          id="type"
          name="type"
          component={SelectFieldAdapter}
          label={t('type', { ns: 'labels' })}
          options={planTypeOptions}
          fullWidth
          required
        />
      </Grid>
      <Grid xs={12} sm={6}>
        <Field
          id="platform"
          name="platform"
          component={SelectFieldAdapter}
          label={t('modality', { ns: 'labels', defaultValue: 'Modalidad' })}
          options={platformOptions}
          fullWidth
          required
        />
      </Grid>
      <Grid xs={12} sm={6}>
        <Field
          id="sessions"
          name="sessions"
          component={TextFieldAdapter}
          label={t('numberOfSessions', { ns: 'labels' })}
          parse={onlyDigits}
          inputProps={{ inputMode: 'numeric' }}
          fullWidth
          required={isRequiredField('sessions', requiredFields)}
        />
      </Grid>
      <Grid xs={12} sm={4}>
        <Field
          id="price_usd"
          name="price_usd"
          component={TextFieldAdapter}
          label={t('priceUsd', { ns: 'labels' })}
          parse={onlyDigits}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
            endAdornment: numbersOnlyAdornment,
            inputProps: { inputMode: 'numeric' }
          }}
          fullWidth
          helperText="Precio en dólares USD"
        />
      </Grid>
      <Grid xs={12} sm={4}>
        <Field
          id="price_cop"
          name="price_cop"
          component={TextFieldAdapter}
          label={t('priceCop', { ns: 'labels' })}
          parse={onlyDigits}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
            endAdornment: numbersOnlyAdornment,
            inputProps: { inputMode: 'numeric' }
          }}
          fullWidth
          helperText="Precio en pesos COP"
        />
      </Grid>
      {/* Stripe (USD) solo se muestra cuando está listo para activarse (REACT_APP_STRIPE_ENABLED). */}
      {STRIPE_ENABLED && (
        <Grid xs={12} sm={4}>
          <Field
            id="stripe_price_id"
            name="stripe_price_id"
            component={TextFieldAdapter}
            label={t('stripePriceId', { ns: 'labels' })}
            fullWidth
            helperText="Suscripción USD: Price recurrente mensual de Stripe (price_…)"
          />
        </Grid>
      )}
      <Grid xs={12}>
        <Field
          id="is_subscribable"
          name="is_subscribable"
          component={CheckboxFieldAdapter}
          label={t(STRIPE_ENABLED ? 'isSubscribableWithStripe' : 'isSubscribable', { ns: 'labels' })}
        />
      </Grid>

      <Grid xs={12}>
        <Field
          id="description"
          name="description"
          component={TextAreaFieldAdapter}
          label={t('description', { ns: 'labels' })}
          helperText={t('text', { ns: 'validations' })}
          multiline
          minRows={5}
          type="text"
          inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
          fullWidth
        />
      </Grid>

      <PlanCategoryDropdown />

      <Grid xs={12}>
        <Field
          id="image"
          name="image"
          component={ImageUploadAdapter}
          label="Imagen del plan · JPG, PNG · Relación 16:9 (ej. 1280×720) · Máx. 2 MB"
          allowedFileTypes={{
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg']
          }}
          crop
          cropAspect={16 / 9}
          cropShape="rect"
        />
      </Grid>
    </Grid>
  );
}

PlanFields.propTypes = {
  requiredFields: PropTypes.array.isRequired
};

export default PlanFields;
